/**
 * ConnectMultipleSitesModal - Agency-only popup for managing connected shops.
 *
 * All shop actions (create, edit, delete) happen inside this popup.
 * Each action triggers its own API request; spinners show on the relevant button during pending requests.
 *
 * Data structure (single source of truth): { url, name, slug }[]
 */
import React, { useState, useEffect } from 'react';
import '../../styles/onboardingFlow/onboardingComponents/multipleSitesModal.scss';
import GlobeSimpleIcon from '../../assets/onboarding/common/GlobeSimple.svg';
import TrashIcon from '../../assets/onboarding/common/Trash.svg';
import LightBulbIcon from '../../assets/onboarding/common/light-bulb.svg';

/** Shop mapping: single source of truth for connected shops in the popup */
export interface ConnectedShop {
  url: string;
  name: string;
  slug: string;
  id?: string; // optional; from API when creating
}

interface ConnectMultipleSitesModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Initial shops (e.g. from init API rehydration) */
  shops: ConnectedShop[];
  /** Called when shops change (create/edit/delete) */
  onShopsChange: (shops: ConnectedShop[]) => void;
  /** Create shop API - returns new shop or null on failure */
  onCreateShop: (url: string) => Promise<ConnectedShop | null>;
  /** Update shop API - returns success */
  onUpdateShop: (slug: string, url: string) => Promise<boolean>;
  /** Delete shop API - returns success */
  onDeleteShop: (slug: string) => Promise<boolean>;
  /** Max shops allowed (e.g. BE limit during onboarding). Default 5. */
  maxShops?: number;
}

const DEFAULT_MAX_SHOPS = 5;

const ConnectMultipleSitesModal: React.FC<ConnectMultipleSitesModalProps> = ({
  isOpen,
  onClose,
  shops: initialShops,
  onShopsChange,
  onCreateShop,
  onUpdateShop,
  onDeleteShop,
  maxShops: maxShopsProp,
}) => {
  const maxShops = maxShopsProp ?? DEFAULT_MAX_SHOPS;
  // Single source of truth for popup state
  const [shops, setShops] = useState<ConnectedShop[]>(initialShops);
  const [newSiteUrl, setNewSiteUrl] = useState('');
  const [showMessageBox, setShowMessageBox] = useState(true);

  // Create: spinner on Add button while POST is pending
  const [isCreating, setIsCreating] = useState(false);
  // Edit: which shop slug is being edited; isSavingEdit = spinner on Save button
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [editUrl, setEditUrl] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  // Delete: which shop slug is being deleted, spinner on that Delete button
  const [deletingSlug, setDeletingSlug] = useState<string | null>(null);

  // Sync with parent when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowMessageBox(true);
      setShops(initialShops);
      setEditingSlug(null);
      setDeletingSlug(null);
    }
  }, [isOpen, initialShops]);

  /** Handle Add (+) button: immediately POST to create shop */
  const handleAddShop = async () => {
    const url = newSiteUrl.trim().replace(/^https?:\/\//, '');
    if (!url || shops.length >= maxShops) return;
    if (
      shops.some(
        (s) =>
          s.url?.includes(url) ||
          url.includes(s.url?.replace(/^https?:\/\//, '') ?? '')
      )
    )
      return;

    setIsCreating(true);
    try {
      const created = await onCreateShop(url);
      if (created) {
        const updated = [...shops, created];
        setShops(updated);
        onShopsChange(updated);
        setNewSiteUrl('');
      }
    } finally {
      setIsCreating(false);
    }
  };

  /** Handle Edit: update shop URL via PUT */
  const handleStartEdit = (shop: ConnectedShop) => {
    setEditingSlug(shop.slug);
    setEditUrl(shop.url?.replace(/^https?:\/\//, '') ?? '');
  };

  const handleSaveEdit = async () => {
    if (!editingSlug) return;
    const url = editUrl.trim();
    if (!url) return;

    setIsSavingEdit(true);
    try {
      const success = await onUpdateShop(editingSlug, url);
      if (success) {
        const fullUrl = url.startsWith('http') ? url : `https://${url}`;
        const updated = shops.map((s) =>
          s.slug === editingSlug ? { ...s, url: fullUrl } : s
        );
        setShops(updated);
        onShopsChange(updated);
        setEditingSlug(null);
      }
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingSlug(null);
    setEditUrl('');
  };

  /** Handle Delete: DELETE shop, remove from list */
  const handleDeleteShop = async (slug: string) => {
    setDeletingSlug(slug);
    try {
      const success = await onDeleteShop(slug);
      if (success) {
        const updated = shops.filter((s) => s.slug !== slug);
        setShops(updated);
        onShopsChange(updated);
      }
    } finally {
      setDeletingSlug(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, action: 'add' | 'edit') => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (action === 'add' && shops.length < maxShops) handleAddShop();
      if (action === 'edit' && editingSlug) handleSaveEdit();
    }
  };

  if (!isOpen) return null;

  const normalizedNewUrl = newSiteUrl.trim().replace(/^https?:\/\//, '');
  const canAdd =
    normalizedNewUrl &&
    shops.length < maxShops &&
    !shops.some(
      (s) =>
        (s.url ?? '').replace(/^https?:\/\//, '').toLowerCase() ===
        normalizedNewUrl.toLowerCase()
    );

  return (
    <div className='multipleSitesModal-overlay' onClick={onClose}>
      {/* Info message box */}
      {showMessageBox && (
        <div
          className='multipleSitesModal-messageBox'
          onClick={(e) => e.stopPropagation()}
        >
          <div className='multipleSitesModal-messageBox__icon'>
            <img src={LightBulbIcon} alt='Light bulb' />
          </div>
          <div className='multipleSitesModal-messageBox__content'>
            <p className='multipleSitesModal-messageBox__title'>
              Not ready yet? You can link more sites anytime
            </p>
            <p className='multipleSitesModal-messageBox__text'>
              At least one website is required to proceed. Additional sites can
              be added by clicking the &quot;+&quot; button here or later from
              your dashboard.
            </p>
          </div>
          <button
            className='multipleSitesModal-messageBox__close'
            onClick={() => setShowMessageBox(false)}
            aria-label='Close message'
          >
            <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
              <path
                d='M12 4L4 12M4 4L12 12'
                stroke='#667085'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        </div>
      )}

      <div className='multipleSitesModal' onClick={(e) => e.stopPropagation()}>
        <div className='multipleSitesModal__header'>
          <h2 className='multipleSitesModal__title'>Connect Multiple Sites</h2>
          <button
            className='multipleSitesModal__close'
            onClick={onClose}
            aria-label='Close'
          >
            <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
              <path
                d='M18 6L6 18M6 6L18 18'
                stroke='#667085'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        </div>

        <p className='multipleSitesModal__description'>
          Add all the websites you want to connect below.
        </p>

        {/* Input + Add button: Add triggers POST immediately */}
        <div className='multipleSitesModal__input-container'>
          <div className='multipleSitesModal__input-group'>
            <span className='multipleSitesModal__prefix'>https://</span>
            <input
              type='text'
              className='multipleSitesModal__input'
              placeholder='brandsite.com'
              value={newSiteUrl}
              onChange={(e) => setNewSiteUrl(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, 'add')}
              disabled={shops.length >= maxShops}
            />
            <button
              className='multipleSitesModal__add-button'
              onClick={handleAddShop}
              type='button'
              disabled={!canAdd || isCreating}
              title={
                shops.length >= maxShops
                  ? `Maximum ${maxShops} sites allowed`
                  : ''
              }
            >
              {isCreating ? (
                <span className='multipleSitesModal__spinner' />
              ) : (
                <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
                  <path
                    d='M10 4V16M4 10H16'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* List of connected shops: Edit (before Delete) and Delete */}
        <div className='multipleSitesModal__sites-list'>
          {shops.map((shop) => (
            <div key={shop.slug} className='multipleSitesModal__site-item'>
              <div className='multipleSitesModal__site-icon'>
                <img src={GlobeSimpleIcon} alt='Globe' />
              </div>

              {editingSlug === shop.slug ? (
                <>
                  <span className='multipleSitesModal__prefix'>https://</span>
                  <input
                    type='text'
                    className='multipleSitesModal__input multipleSitesModal__input--inline'
                    value={editUrl}
                    onChange={(e) => setEditUrl(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, 'edit')}
                    autoFocus
                  />
                  <button
                    className='multipleSitesModal__edit-save'
                    onClick={handleSaveEdit}
                    type='button'
                    disabled={isSavingEdit || !editUrl.trim()}
                  >
                    {isSavingEdit ? (
                      <span className='multipleSitesModal__spinner multipleSitesModal__spinner--sm' />
                    ) : (
                      'Save'
                    )}
                  </button>
                  <button
                    className='multipleSitesModal__edit-cancel'
                    onClick={handleCancelEdit}
                    type='button'
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span className='multipleSitesModal__site-url'>
                    {shop.url?.replace(/^https?:\/\//, '') ?? shop.name}
                  </span>
                  <button
                    className='multipleSitesModal__edit-button'
                    onClick={() => handleStartEdit(shop)}
                    type='button'
                    aria-label='Edit site'
                    disabled={!!deletingSlug}
                  >
                    <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
                      <path
                        d='M11.333 2.00001C11.5084 1.82445 11.7163 1.68507 11.9446 1.59128C12.1728 1.49749 12.4171 1.45117 12.6633 1.45523C12.9096 1.4593 13.1528 1.51368 13.3778 1.61478C13.6027 1.71589 13.805 1.86153 13.9717 2.04312C14.1383 2.22471 14.2658 2.43852 14.3469 2.67087C14.428 2.90321 14.461 3.14919 14.4437 3.39402C14.4264 3.63885 14.3591 3.87737 14.2459 4.09405C14.1328 4.31073 13.9762 4.50094 13.7863 4.65334L6.33301 12.1067L2.66634 13.3333L3.89301 9.66668L11.333 2.00001Z'
                        stroke='#667085'
                        strokeWidth='1.33333'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </button>
                  <button
                    className='multipleSitesModal__remove-button'
                    onClick={() => handleDeleteShop(shop.slug)}
                    type='button'
                    aria-label='Delete site'
                    disabled={!!deletingSlug || !!editingSlug}
                  >
                    {deletingSlug === shop.slug ? (
                      <span className='multipleSitesModal__spinner multipleSitesModal__spinner--sm' />
                    ) : (
                      <img src={TrashIcon} alt='Delete' />
                    )}
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        <div className='multipleSitesModal__footer'>
          <button
            className='multipleSitesModal__button multipleSitesModal__button--secondary'
            onClick={onClose}
            type='button'
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConnectMultipleSitesModal;
